import React from "react";
import { DropdownMenu } from "@/components/common/DropdownMenu";
import {
  MenuDivider,
  MenuGroupLabel,
  MenuItem,
} from "@/components/common/MenuItem";
import { FAMILIES } from "@/components/canvas/actionBar/families";
import {
  ACTIONS,
  type ActionId,
  type Choice,
} from "@/components/canvas/actionBar/registry";
import { entryKeyOf, type Slot } from "@/components/canvas/actionBar/arrange";
import { CATALOG } from "@/actions/catalog";
import { BAR_GAP } from "@/components/canvas/overlays/barGap";

interface MoreMenuProps {
  slot: Slot;
  onPick: (id: ActionId) => void;
  onClose: () => void;
  anchor: HTMLElement | null;
}

/** A menu hung above a slot of the action bar. */
export const BarMenu = ({
  label,
  anchor,
  onClose,
  children,
}: {
  label: string;
  anchor: HTMLElement | null;
  onClose: () => void;
  children: React.ReactNode;
}) => (
  <DropdownMenu
    anchorRef={{ current: anchor }}
    edgeRef={{
      current: anchor?.closest<HTMLElement>(".canvas-action-toolbar") ?? null,
    }}
    gap={BAR_GAP}
    isOpen
    onClose={onClose}
    label={label}
    placement="top-center"
    className="canvas-action-toolbar__menu"
  >
    {children}
  </DropdownMenu>
);

/** ⋮'s rows, a divider between families and before a destructive one. */
export function MoreMenu({
  slot,
  onPick,
  onClose,
  anchor,
}: MoreMenuProps): React.JSX.Element {
  const { members } = slot;
  return (
    <BarMenu
      label={FAMILIES[slot.family].label}
      anchor={anchor}
      onClose={onClose}
    >
      {members.map((id, i) => (
        <React.Fragment key={id}>
          {i > 0 &&
            (ACTIONS[id].destructive ||
              ACTIONS[id].family !== ACTIONS[members[i - 1]].family) && (
              <MenuDivider />
            )}
          <MenuItem
            id={`canvas-action-${id}`}
            Icon={CATALOG[entryKeyOf(slot, id)].Icon}
            label={CATALOG[entryKeyOf(slot, id)].label}
            onClick={() => onPick(id)}
          />
        </React.Fragment>
      ))}
    </BarMenu>
  );
}

/** A menu member's choices: Import's sources, Export's formats. */
export function ChoiceMenu({
  id,
  heading,
  choices,
  onPick,
  onClose,
  anchor,
}: {
  id: ActionId;
  heading?: string;
  choices: Choice[];
  onPick: (choice: Choice) => void;
  onClose: () => void;
  anchor: HTMLElement | null;
}): React.JSX.Element {
  const rows = choices.map((choice) => (
    <MenuItem
      key={choice.key}
      Icon={CATALOG[choice.entry].Icon}
      label={choice.label ?? CATALOG[choice.entry].label}
      onClick={() => onPick(choice)}
    />
  ));
  return (
    <BarMenu
      label={FAMILIES[ACTIONS[id].family].label}
      anchor={anchor}
      onClose={onClose}
    >
      {heading ? (
        <div role="group" aria-labelledby={`bar-${id}-heading`}>
          <div id={`bar-${id}-heading`}>
            <MenuGroupLabel label={heading} />
          </div>
          {rows}
        </div>
      ) : (
        rows
      )}
    </BarMenu>
  );
}
